import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import styles from './BondsList.module.css';
import Loader from '../Loader';

interface Bond {
  _id: string;
  id_issuer: string;
  isin: string;
  issuer_name: string;
  title?: string;
  issue_date?: string;
  maturity_date?: string;
  currency?: string;
  volume_issued?: number;
  coupon_rate?: number;
  green_bond?: boolean;
  social_bond?: boolean;
  sustainability_bond?: boolean;
  sustainability_linked_bond?: boolean;
  sdg_numbers?: number[];
  tags?: string[];
  public?: boolean;
  update_timestamp?: string;
}

interface BondsListProps {
  bonds: Bond[];
  loading?: boolean;
  onTagClick?: (tag: string) => void;
  onSDGClick?: (sdgNumber: number) => void;
  emptyMessage?: string;
  className?: string;
}

/**
 * BondsList - List view for displaying bonds with limited details for public users
 */
const BondsList: React.FC<BondsListProps> = ({
  bonds,
  loading = false,
  emptyMessage,
  className = ''
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useAuth();

  const formatShortDate = (dateString?: string) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatVolume = (volume?: number, currency?: string) => {
    if (!volume) return '—';
    const curr = currency || 'EUR';
    const millions = volume / 1000000;

    if (millions >= 1) {
      return `${curr} ${millions.toFixed(0)}`;
    }
    return `${curr} ${(volume / 1000).toFixed(0)}`;
  };

  const formatUpdateDate = (dateString?: string) => {
    if (!dateString) return '—';
    try {
      return formatShortDate(dateString);
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <Loader />
        <p className={styles.loadingText}>
          {t('bonds.loading', 'Loading ...')}
        </p>
      </div>
    );
  }

  if (!bonds || bonds.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>📊</div>
        <h3 className={styles.emptyTitle}>
          {t('bonds.empty.title', 'No bonds found')}
        </h3>
        <p className={styles.emptyMessage}>
          {emptyMessage || t('bonds.empty.message', 'Try adjusting your filters or search criteria.')}
        </p>
      </div>
    );
  }

  const handleRowClick = (bondId: string) => {
    navigate(`/bonds/${bondId}`);
  };

  // Add updated timestamp property to the Bond interface
  interface ExtendedBond extends Bond {
    update_timestamp?: string;
  }

  const extendedBonds = bonds as ExtendedBond[];

  return (
    <div className={`${styles.container} ${className}`}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.headerIsin}>{t('bonds.isin', 'ISIN')}</th>
            <th className={styles.headerIssuer}>{t('bonds.issuer', 'ISSUER')}</th>
            <th className={styles.headerIssued}>{t('bonds.issued', 'ISSUED')}</th>
            <th className={styles.headerMaturity}>{t('bonds.maturity', 'MATURITY')}</th>
            <th className={styles.headerVolume}>{t('bonds.volumeMillions', 'VOLUME (MILLION)')}</th>
            <th className={styles.headerUpdated}>{t('bonds.updated', 'UPDATED')}</th>
          </tr>
        </thead>
        <tbody>
          {extendedBonds.map((bond) => (
            <tr
              key={bond._id}
              className={styles.clickableRow}
              onClick={() => handleRowClick(bond._id)}
            >
              <td className={styles.isinCell}>
                <span className={styles.isinCode}>{bond.isin || '—'}</span>
              </td>
              <td className={styles.issuerCell}>{bond.issuer_name || '—'}</td>
              <td className={styles.dateCell}>{formatShortDate(bond.issue_date)}</td>
              <td className={styles.dateCell}>{formatShortDate(bond.maturity_date)}</td>
              <td className={styles.volumeCell}>{formatVolume(bond.volume_issued, bond.currency)}</td>
              <td className={styles.dateCell}>{formatUpdateDate(bond.update_timestamp || bond.issue_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BondsList;